import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Button,
  Field,
  Input,
  Spinner,
  Text,
  makeStyles,
} from "@fluentui/react-components";
import apiServices from "../service";
import LogoNew from "../assets/logo/assystCloudLogo.png";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    gap: "16px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    width: "320px",
  },
});

const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// Same list LoginPage/index.tsx uses to decide whether to ask for an
// organization name — a public email domain can't be mapped to a single
// tenant by domain alone.
const PUBLIC_EMAIL_DOMAINS = ["gmail.com", "yahoo.com"];

/**
 * This is the OAuth `authorization_endpoint` advertised in
 * /.well-known/oauth-authorization-server (see MCP_UI_LOGIN_URL). A real MCP
 * client (confirmed against Claude Code) opens this via a plain GET with
 * standard OAuth2 query params — client_id, redirect_uri, state,
 * code_challenge, code_challenge_method, scope — after registering itself via
 * Dynamic Client Registration. Collects an email (for realm resolution),
 * calls /asc-auth/mcp-login with those same params, then redirects to the
 * Keycloak login page it returns.
 */
const McpLoginPage: React.FC = () => {
  const styles = useStyles();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get("client_id");
  const redirectUri = searchParams.get("redirect_uri");
  const clientState = searchParams.get("state");
  const codeChallenge = searchParams.get("code_challenge") || undefined;
  const codeChallengeMethod = searchParams.get("code_challenge_method") || undefined;
  const scope = searchParams.get("scope") || undefined;

  const [email, setEmail] = useState("");
  const [org, setOrg] = useState("");
  const [needsOrg, setNeedsOrg] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const domain = email.split("@")[1]?.toLowerCase();
    if (domain && PUBLIC_EMAIL_DOMAINS.includes(domain)) {
      setNeedsOrg(true);
    }
  }, [email]);

  if (!clientId || !redirectUri || !clientState) {
    return (
      <div className={styles.container}>
        <Text size={400} style={{ color: "#d13438" }}>
          This page must be opened by an MCP client — missing required OAuth parameters.
        </Text>
      </div>
    );
  }

  const emailValid = EMAIL_RE.test(email);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailValid) return;

    setSubmitting(true);
    setError("");
    apiServices.auth
      .mcpLogin({
        email,
        client_id: clientId,
        redirect_uri: redirectUri,
        state: clientState,
        code_challenge: codeChallenge,
        code_challenge_method: codeChallengeMethod,
        scope,
        org: needsOrg ? org : undefined,
      })
      .then((response) => {
        const redirectUrl = response.data?.redirectUrl;
        if (!redirectUrl) {
          setError("No redirect URL returned — cannot continue.");
          setSubmitting(false);
          return;
        }
        window.location.href = redirectUrl;
      })
      .catch((err: any) => {
        const detail = err?.response?.data?.error;
        if (detail === "organization_required") {
          setNeedsOrg(true);
          setError("Please enter your organization name.");
        } else if (detail === "organization_not_found") {
          setError("Organization not found — please check the name and try again.");
        } else {
          setError(`Sign-in failed: ${detail || "Please try again."}`);
        }
        setSubmitting(false);
      });
  };

  return (
    <div className={styles.container}>
      <img src={LogoNew} alt="logo" width={220} />
      <form className={styles.form} onSubmit={handleSubmit}>
        <Field
          label="Email"
          validationState={email && !emailValid ? "error" : "none"}
          validationMessage={email && !emailValid ? "Please enter a valid email" : ""}
        >
          <Input
            size="large"
            placeholder="you@company.com"
            value={email}
            onChange={(_, data) => setEmail(data.value)}
            autoComplete="off"
            disabled={submitting}
          />
        </Field>
        {needsOrg && (
          <Field label="Organisation">
            <Input
              size="large"
              placeholder="Your organization name"
              value={org}
              onChange={(_, data) => setOrg(data.value)}
              autoComplete="off"
              disabled={submitting}
            />
          </Field>
        )}
        <Button
          appearance="primary"
          type="submit"
          disabled={!emailValid || (needsOrg && !org) || submitting}
        >
          {submitting ? <Spinner size="tiny" /> : "Continue"}
        </Button>
        {error && (
          <Text size={300} style={{ color: "#d13438" }}>
            {error}
          </Text>
        )}
      </form>
    </div>
  );
};

export default McpLoginPage;
